<template>
  <RegisterForm
    :title="$t('verificationStatus.idVerification.idInformation.stepTitle')"
    :nextButtonOnClick="nextButtonOnClick"
    verticalPadding="py-0"
  >
    <div class="mt-5 md:mt-10">
      <IdInformationComponent
        ref="form"
        radioLabel="verificationStatus.idVerification.idInformation.title"
        :idTypeOptions="idTypeOptions"
        :isShowAddressInputs="isShowAddressInputs"
        :disabled="isDisabled"
      /></div
  ></RegisterForm>
</template>

<script>
import IdInformationComponent, { idType } from '@/components/register/registerForms/uploadForms/IdInformation.vue';
import RegisterForm from '@/components/register/registerForms/RegisterForm.vue';
import registerStep from '@/constants/register/registerStep';
import registerRepository from '@/repositories/register/registerRepository';
import poiStore from '@/store/register/poiVerification/poiStore';
import auditStatus from '@/constants/auditStatus';
import { countryCodeEnum } from '@/constants/country';
import { mapState } from 'vuex';
import { RegisterRoutePath } from '@/router/registerRoutes';
import { sensorsTrack } from '@/util/sensorsdata';
import { SENSORS_TRACK } from '@/util/sensorsdata/constant';

export default {
  name: 'IdInformation',
  components: { IdInformationComponent, RegisterForm },
  data() {
    return {
      isShowAddressInputs: false,
      isCallId3: false
    };
  },
  created() {
    // Need call id3 for 1st time
    this.id3CountryList = [countryCodeEnum.UNITED_KINGDOM, countryCodeEnum.FRANCE, countryCodeEnum.MALAYSIA];
    // Malaysia no need fill in address (subset of this.id3CountryList)
    this.showCountryFormList = [countryCodeEnum.UNITED_KINGDOM];
    this.idTypeOptions = [
      {
        value: idType.id,
        label: 'verificationStatus.idInformation.idCard'
      },
      {
        value: idType.passport,
        label: 'verificationStatus.idInformation.passport'
      },
      {
        value: idType.other,
        label: 'verificationStatus.idInformation.other',
        infoText: `verificationStatus.idInformation.otherInfoText`
      }
    ];
  },
  mounted() {
    if (this.activeTab === registerStep.idInformation) {
      this.init();
    }
  },
  computed: {
    // Pending record
    poiPendingRecord() {
      return poiStore.state.poiPendingRecord;
    },
    isDisabled() {
      return this.poiAuditStatus === auditStatus.pending;
    },
    countryId() {
      return poiStore.state.countryId;
    },
    idInformationForm() {
      return poiStore.state.idInformationForm;
    },
    poiAuditStatus() {
      return this.$store.state.common.poiAuditStatus;
    },
    ...mapState('register', ['currStep', 'activeTab'])
  },
  methods: {
    async init() {
      // Pendig record
      if (this.poiAuditStatus === auditStatus.pending) {
        this.$refs.form.form = {
          ...this.$refs.form.form,
          ...this.poiPendingRecord
        };
        poiStore.dispatch('setIdInformationForm', this.poiPendingRecord);
      } else {
        if (this.idInformationForm === null) {
          const response = await poiStore.dispatch('actionGetData');
          if (response instanceof Error) {
            this.$message({
              message: response.message,
              type: 'error'
            });
            this.$router.push(RegisterRoutePath.register);
          }
        }

        // Set sub step for Step 5
        // If never submitted and is completed 3.1
        if (
          this.currStep === registerStep.idInformation &&
          this.poiAuditStatus === auditStatus.init &&
          this.idInformationForm?.idNumber?.length > 0
        ) {
          this.$store.dispatch('register/setStep', registerStep.idUpload);
        }

        // Init form data
        const countryId = this.countryId;
        // Not call id3 if completed this step be4
        this.isCallId3 =
          !(this.idInformationForm?.idNumber?.length > 0) &&
          countryId !== null &&
          this.id3CountryList.includes(parseInt(countryId));
        this.isShowAddressInputs = this.isCallId3 && this.showCountryFormList.includes(parseInt(countryId));

        if (this.$refs.form?.form) {
          if (this.isShowAddressInputs) {
            this.$refs.form.form.countryId = countryId;
          }

          this.$refs.form.form = {
            ...this.$refs.form.form,
            ...this.idInformationForm
          };
        }
      }
    },
    nextButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV2,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV2_ID_INFORMATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV2_NEXT
      })
      if (this.isDisabled) {
        this.$store.dispatch('register/setActiveTab', registerStep.idUpload);
      } else {
        this.$refs.form?.validate(async valid => {
          if (valid) {
            const form = this.$refs?.form?.form ?? {};
            const { address = null, ...formWithoutAddress } = form;

            // No pass address since it is street name in general POI
            const response = await registerRepository.updateUserIdAddress(formWithoutAddress);

            if (response instanceof Error) {
              this.$message({
                message: response.message,
                type: 'error'
              });
            } else {
              let isId3Pass = false;
              let isSuccessResponse = true;

              if (this.isCallId3) {
                const { address = null, streetNumber = null, suburb = null, state = null, postcode = null } = form;

                const id3Response = await registerRepository.doId3({
                  address,
                  streetNumber,
                  suburb,
                  state,
                  postcode
                });

                if (response instanceof Error) {
                  this.$message({
                    message: response.message,
                    type: 'error'
                  });
                  isSuccessResponse = false;
                } else {
                  isId3Pass = id3Response;
                }
              }

              if (isSuccessResponse) {
                this.isCallId3 = false;
                this.isShowAddressInputs = false;

                poiStore.dispatch('setIdInformationForm', this.$refs.form.form);
                if (isId3Pass) {
                  this.$store.commit('common/setId3Pass', true);
                  if (this.currStep > registerStep.poaInformation) {
                    this.$store.dispatch('register/setActiveTab', registerStep.poaInformation);
                  } else {
                    this.$store.dispatch('register/setStep', registerStep.poaInformation);
                  }
                  this.$router.push(RegisterRoutePath.identityVerificationComplete);
                } else {
                  if (this.currStep > registerStep.idUpload) {
                    this.$store.dispatch('register/setActiveTab', registerStep.idUpload);
                  } else {
                    this.$store.dispatch('register/setStep', registerStep.idUpload);
                  }
                }
              }
            }
          } else {
            console.log('error submit!!');
          }
        });
      }
    }
  }
};
</script>
